import type { BalanceResponseBody } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import { daysUntil, formatUsdMicrosAsCnyDisplay } from './formatters.ts';

/**
 * "含赠送 ¥200.00 · 7 天后到期" — the one line that answers, on the balance
 * itself, which part of the money was given and when it disappears.
 *
 * Two rules shape it:
 *
 * 1. **Absent means absent.** Every split field on `/v1/billing/balance` is
 *    optional. Platform deploys before the consoles, so a console can meet a
 *    platform that has not shipped the split yet; when `promoUsdMicros` is
 *    missing this renders nothing rather than a confident "¥0.00 gift".
 * 2. **The date belongs to one batch.** `promoNextExpiringUsdMicros` is the
 *    soonest-expiring lot alone. When it equals the gift total, the whole
 *    gift expires on that date and the line says so plainly; when it is
 *    smaller, the line names the partial amount instead — a tenant holding
 *    two gifts must not read the earliest date against the largest number.
 */
export function GiftBalanceLine({
  balance,
  cnyPerUsd,
  testId,
}: {
  readonly balance: BalanceResponseBody | undefined;
  readonly cnyPerUsd: number | null;
  readonly testId: string;
}): JSX.Element | null {
  const t = useT();
  if (balance === undefined || balance.promoUsdMicros === undefined) {
    return null;
  }
  const promoUsdMicros = balance.promoUsdMicros;
  if (promoUsdMicros <= 0) {
    return null;
  }

  const included = t('billing.gift.included', {
    amount: formatUsdMicrosAsCnyDisplay(promoUsdMicros, cnyPerUsd),
  });
  const expiringUsdMicros = balance.promoNextExpiringUsdMicros ?? 0;
  const when = expiryPhrase(t, balance.promoNextExpiresAt ?? null);

  let detail: string | null = null;
  if (when !== null) {
    detail =
      expiringUsdMicros > 0 && expiringUsdMicros < promoUsdMicros
        ? t('billing.gift.partial', {
            amount: formatUsdMicrosAsCnyDisplay(expiringUsdMicros, cnyPerUsd),
            when,
          })
        : when;
  }

  return (
    <p className="m-0 font-mono text-micro text-warn" data-testid={testId}>
      {detail === null ? included : `${included} · ${detail}`}
    </p>
  );
}

function expiryPhrase(t: ReturnType<typeof useT>, expiresAt: string | null): string | null {
  if (expiresAt === null) {
    return null;
  }
  const days = daysUntil(expiresAt);
  if (days === null) {
    return null;
  }
  if (days < 0) {
    // The sweep runs hourly, so an expired lot can still be outstanding for
    // up to an hour. Say "expired", never "in -1 days".
    return t('billing.gift.expired');
  }
  if (days === 0) {
    return t('billing.gift.expires_today');
  }
  if (days === 1) {
    return t('billing.gift.expires_in_day');
  }
  return t('billing.gift.expires_in_days', { days: String(days) });
}
